import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Alert } from "@/core/components/ui/alert/alert";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { documentCategory } from "./document-categories";
import { ONBOARDING_DOCUMENT_TYPE } from "./types";

interface ReadyChecklistProps {
  hasPayPlan: boolean;
  hasReferenceData: boolean;
  error: string | null;
}

const CategoryState = ({ title, done }: { title: string; done: boolean }) => (
  <Stack direction="row" gap="sm" align="center" className="shrink-0">
    {done ? (
      <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-60" />
    ) : (
      <span
        aria-hidden="true"
        className="h-5 w-5 shrink-0 rounded-full border-2 border-stroke-secondary"
      />
    )}
    <Typography variant="bodyW2" textColor={done ? "primary" : "tertiary"}>
      {title}
    </Typography>
  </Stack>
);

// What finished means, and where they stand against it. A strip under the page header
// rather than a card: it describes the step as a whole, so putting it between the drop
// pane and the action row made it read as another thing to do.
export const ReadyChecklist = ({
  hasPayPlan,
  hasReferenceData,
  error,
}: ReadyChecklistProps) => {
  const payPlanCategory = documentCategory(ONBOARDING_DOCUMENT_TYPE.payPlan);
  const referenceCategory = documentCategory(
    ONBOARDING_DOCUMENT_TYPE.referenceData,
  );

  return (
    <Stack gap="sm" align="stretch" fullWidth>
      <Stack
        direction="row"
        gap="lg"
        align="center"
        fullWidth
        className="flex-wrap"
      >
        <CategoryState title={payPlanCategory.title} done={hasPayPlan} />
        <CategoryState
          title={referenceCategory.title}
          done={hasReferenceData}
        />
        <Typography
          variant="captionW1"
          textColor="tertiary"
          className="min-w-0 flex-1"
        >
          {/* The instruction, not an alternative: there is no version of finished that
              leaves out the rules, so the copy never offers one. */}
          {hasPayPlan
            ? "Both kinds are in. Tell us you are finished when you have sent everything you have."
            : "Add your pay plan to finish. Without the rules written down we would be guessing at how your team is paid."}
        </Typography>
      </Stack>
      {error && <Alert variant="destructive">{error}</Alert>}
    </Stack>
  );
};
